import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { userService } from '@/services/userService';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roleLoading: boolean;
  userRole: 'customer' | 'restaurant_owner' | 'admin' | null;
  isVisitor: boolean;
  signUp: (email: string, password: string, fullName: string, phone?: string, role?: 'customer' | 'restaurant_owner', cpfCnpj?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<'customer' | 'restaurant_owner' | 'admin' | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const navigate = useNavigate();

  // Visitor mode - true when no user is logged in
  const isVisitor = !user && !loading;

  // Cache for validation results to reduce database calls
  const validationCacheRef = useState(new Map<string, { result: boolean; timestamp: number }>())[0];

  useEffect(() => {
    // Check current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!session?.user) {
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Consolidated effect for fetching user role and validating profile integrity in parallel
  // Skip for visitors to improve performance
  useEffect(() => {
    if (isVisitor || !user?.id || userRole !== null) return;
    
    let cancelled = false;
    
    const initializeAuth = async () => {
      setRoleLoading(true);
      
      try {
        // Fetch role e validação em PARALELO
        const [role, isValid] = await Promise.all([
          userService.getCurrentUserRole(),
          userService.validateProfileIntegrity()
        ]);
        
        if (cancelled) return;
        
        if (!isValid) {
          console.error('Profile integrity failed');
          signOut();
          return;
        }
        
        setUserRole(role as 'customer' | 'restaurant_owner' | 'admin' || 'customer');
        
        // Toast apenas se na página de auth
        if (window.location.pathname === '/auth') {
          toast({ title: "Login realizado com sucesso!" });
          handleSmartRedirect();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (!cancelled) {
          setUserRole('customer'); // Fallback
        }
      } finally {
        if (!cancelled) setRoleLoading(false);
      }
    };
    
    initializeAuth();
    
    return () => { cancelled = true; };
  }, [user?.id, isVisitor]);

  // Periodic profile integrity validation (optimized)
  // Skip for visitors to improve performance
  useEffect(() => {
    if (isVisitor || !user?.id) return;

    const validatePeriodically = async () => {
      const isValid = await validateProfileIntegrity();
      if (!isValid) {
        console.error('Periodic profile validation failed - forcing logout');
        signOut();
      }
    };

    // Validate every 20 minutes for active sessions (further reduced for performance)
    const interval = setInterval(validatePeriodically, 20 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user?.id, isVisitor]);

  const validateProfileIntegrity = async (): Promise<boolean> => {
    if (!user?.id) return false;
    
    // Check cache first (5 minute cache)
    const cacheKey = `profile_integrity_${user.id}`;
    const cached = validationCacheRef.get(cacheKey);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < 5 * 60 * 1000) {
      return cached.result;
    }
    
    try {
      const { data, error } = await supabase.rpc('validate_user_profile_integrity');
      
      if (error) {
        console.error('Profile integrity validation error:', error);
        return false;
      }
      
      const result = data === true;
      
      // Cache the result
      validationCacheRef.set(cacheKey, { result, timestamp: now });
      
      return result;
    } catch (error) {
      console.error('Profile integrity validation failed:', error);
      return false;
    }
  };

  // This function is now integrated into the parallel validation above
  // Keeping for backward compatibility but optimized
  const fetchUserRole = async () => {
    if (!user?.id) return;
    
    try {
      const role = await userService.getCurrentUserRole();
      setUserRole(role as 'customer' | 'restaurant_owner' | 'admin' | null);
      return role;
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole(null);
      signOut();
      return null;
    }
  };

  const handleSmartRedirect = () => {
    const currentPath = window.location.pathname;
    
    // Only redirect if user is on auth page
    if (currentPath === '/auth' && userRole) {
      switch (userRole) {
        case 'restaurant_owner':
          navigate('/restaurant');
          break;
        case 'admin':
          navigate('/admin');
          break;
        default:
          navigate('/');
          break;
      }
    }
  };

  const signUp = async (email: string, password: string, fullName: string, phone?: string, role: 'customer' | 'restaurant_owner' = 'customer', cpfCnpj?: string) => {
    // Validar se telefone é obrigatório para customers
    if (role === 'customer' && (!phone || phone.trim().length === 0)) {
      toast({
        variant: "destructive",
        title: "Telefone obrigatório",
        description: "Clientes devem fornecer um telefone para receber atualizações dos pedidos.",
      });
      return { error: new Error("Telefone é obrigatório para clientes") };
    }

    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          phone: phone,
          user_role: role,
          cpf_cnpj: cpfCnpj,
        }
      }
    });
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Erro no cadastro",
        description: error.message,
      });
    } else {
      toast({
        title: "Cadastro realizado!",
        description: "Verifique seu email para ativar a conta.",
      });
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Erro no login",
        description: error.message,
      });
    }
    
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Erro ao sair",
        description: error.message,
      });
    } else {
      toast({
        title: "Logout realizado",
        description: "Até a próxima!",
      });
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Erro ao enviar email",
        description: error.message,
      });
    } else {
      toast({
        title: "Email enviado!",
        description: "Verifique sua caixa de entrada para redefinir a senha.",
      });
    }
    
    return { error };
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      roleLoading,
      userRole,
      isVisitor,
      signUp,
      signIn,
      signOut,
      resetPassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}